import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Typography
} from "antd"
import Fieldset from "components/Fieldset"
import moment, { Moment } from "moment"
import { IPolicyholder, PolicyholderType } from "platform-client/types"
import { mustBeValidPhoneNumber } from "utils/validators/must-be-valid-phone-number"
import { mustBeValidEmail } from "utils/validators/must-be-valid-email"
import { noFutureDates } from "utils/validators/no-future-dates"
import { validateMinAge } from "utils/validators/validate-min-age"
import styled from "styled-components"
import { useCallback, useEffect, useState, useMemo } from "react"
import { UniversalAddress } from "components/UniversalAddress"
import usePlatform from "contexts/platform/use-platform"
import { useQuery } from "react-query"
import { useUserContext } from "contexts/user/context"
import { usePhoneLookupService } from "utils/phoneLookupService"
import { useConfigurationContext } from "contexts/configuration/context"

interface IClientOption {
  label: string
  value: PolicyholderType
}

const clientOptions: IClientOption[] = [
  {
    label: "Individual",
    value: "Individual"
  },
  {
    label: "Business",
    value: "Business"
  }
]

const StyledFieldset = styled(Fieldset)`
  legend {
    color: rgba(0, 0, 0, 0.85);
    font-size: 14px;
  }

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`

const StyledWidthFormItem = styled(Form.Item)<{ $width?: string }>`
  width: calc(${({ $width }) => $width || "50%"} - 10px);
  margin-left: 5px;
  margin-right: 5px;
  padding-bottom: 10px;
`

// starts a new line in a fieldset, so showing or hiding a field doesn't move the fields on the following lines
const StyledLineBreak = styled.div`
  flex-basis: 100%;
  height: 0;
`

const ClientForm = ({
  onBack,
  onFinish,
  onCloseForm,
  isLoading = false,
  client,
  isEditMode = false
}: IClientFormProps): JSX.Element => {
  const [form] = Form.useForm()
  const platform = usePlatform()
  const { user: currentUser } = useUserContext()
  const { lookup: lookupPhoneNumber } = usePhoneLookupService()
  const { flags } = useConfigurationContext()
  const [clientType, setClientType] = useState<
    PolicyholderType | null | undefined
  >(client?.policyholderType || "Individual")
  const [isCheckingPhoneNumber, setIsCheckingPhoneNumber] = useState(false)
  const [selectedOwnerID, setSelectedOwnerID] = useState<string | undefined>()

  const accountManagersQuery = useQuery(
    ["accountManagers", platform.token],
    () => platform.organizationManagement.getUsersByRole("Account Manager"),
    {
      enabled: !!platform.token
    }
  )

  const accountManagerOptions = useMemo(() => {
    if (!accountManagersQuery.data) return []
    return accountManagersQuery.data.map((user) => ({
      label: user.fullName || user.username || "",
      value: user.id
    }))
  }, [accountManagersQuery.data])

  // the branches (offices) of the account manager the current user can create the client in
  const getOwnerBranches = useCallback(
    (ownerID?: string) =>
      accountManagersQuery.data?.find((user) => user.id === ownerID)?.branches,
    [accountManagersQuery.data]
  )

  const ownerBranches = getOwnerBranches(selectedOwnerID) ?? []
  const showOfficePicker = !isEditMode && ownerBranches.length > 1

  // a single branch is selected for the user, otherwise they must choose one
  const getDefaultCompanyID = useCallback(
    (ownerID?: string) => {
      const branches = getOwnerBranches(ownerID)
      return branches?.length === 1 ? branches[0].id : undefined
    },
    [getOwnerBranches]
  )

  const onOwnerChange = (ownerID?: string) => {
    setSelectedOwnerID(ownerID)
    form.setFieldsValue({ companyID: getDefaultCompanyID(ownerID) })
  }

  const isCurrentUserAccountManager = useMemo(() => {
    if (!currentUser?.id || !accountManagersQuery.data) return false
    return accountManagersQuery.data.some((user) => user.id === currentUser.id)
  }, [currentUser?.id, accountManagersQuery.data])

  const mapClientData = (client?: IPolicyholder): IClientData | undefined => {
    if (client === undefined) {
      return undefined
    }

    return {
      policyholderType: client.policyholderType || "Individual",
      businessName: client.businessName || null,
      title: client.contact.name.title || null,
      firstName: client.contact.name.firstName || null,
      lastName: client.contact.name.lastName || null,
      dateOfBirth: client.contact.dateOfBirth
        ? moment(client.contact.dateOfBirth)
        : null,
      phoneNumber: client.contact.phoneNumber || null,
      emailAddress: client.contact.emailAddress || null,
      contactAddress: client.contactAddress
        ? {
            ...client.contactAddress,
            country: JSON.stringify(client.contactAddress.country)
          }
        : undefined,
      ownerID: client.ownerID || undefined,
      referenceID: client.referenceID || null
    }
  }

  useEffect(() => {
    const values = mapClientData(client)
    const initialClientType = values?.policyholderType || "Individual"

    let ownerIDValue: string | undefined
    if (values?.ownerID) {
      ownerIDValue = accountManagerOptions.some(
        (opt) => opt.value === values.ownerID
      )
        ? values.ownerID
        : undefined
    } else if (!isEditMode && isCurrentUserAccountManager && currentUser?.id) {
      ownerIDValue = currentUser.id
    }

    form.setFieldsValue({
      ...values,
      policyholderType: initialClientType,
      ownerID: ownerIDValue,
      companyID: isEditMode ? undefined : getDefaultCompanyID(ownerIDValue)
    })
    setSelectedOwnerID(ownerIDValue)
    setClientType(initialClientType)
  }, [
    form,
    client,
    accountManagerOptions,
    getDefaultCompanyID,
    isCurrentUserAccountManager,
    currentUser?.id,
    isEditMode
  ])

  return (
    <Form form={form} onFinish={onFinish} layout="vertical">
      <Row justify="center">
        <Col span={12} style={{ padding: "50px 15px 50px 15px" }}>
          <Typography.Title level={3}>Client details</Typography.Title>
          <Typography.Paragraph>
            {isEditMode
              ? "Complete the form below to update the client details."
              : "Complete the form below to add the client to the system."}
          </Typography.Paragraph>

          <StyledFieldset title="General">
            <StyledWidthFormItem
              name="policyholderType"
              label="Client Type:"
              required
              rules={[{ required: true, message: "Client Type is required" }]}
              $width="50%"
            >
              <Select
                options={clientOptions}
                onChange={(value) => setClientType(value as PolicyholderType)}
              />
            </StyledWidthFormItem>
            {clientType === "Business" && (
              <StyledWidthFormItem
                name="businessName"
                label="Business name:"
                required={clientType === "Business"}
                rules={[
                  { whitespace: true, message: "Business Name is required" }
                ]}
                $width="50%"
              >
                <Input />
              </StyledWidthFormItem>
            )}
            <StyledLineBreak />
            <StyledWidthFormItem
              name="ownerID"
              label="Account Manager:"
              required
              rules={[
                { required: true, message: "Account Manager is required" },
                {
                  // a user working in a branch can only create clients in branches they can read
                  validator: async (_, ownerID?: string) => {
                    const branches = getOwnerBranches(ownerID)
                    if (
                      !isEditMode &&
                      ownerID &&
                      currentUser?.company &&
                      branches?.length === 0
                    ) {
                      throw new Error(
                        "This account manager isn't in an office you can access"
                      )
                    }
                  }
                }
              ]}
              $width="50%"
            >
              <Select
                showSearch
                allowClear
                placeholder="Select account manager"
                options={accountManagerOptions}
                onChange={(value) => onOwnerChange(value as string | undefined)}
                loading={accountManagersQuery.isLoading}
                disabled={isEditMode && client?.ownerID !== undefined}
                notFoundContent={
                  accountManagersQuery.isLoading
                    ? "Loading..."
                    : "No account managers found"
                }
                filterOption={(input, option) => {
                  const label =
                    typeof option?.label === "string"
                      ? option.label
                      : String(option?.label ?? "")
                  return label.toLowerCase().includes(input.toLowerCase())
                }}
              />
            </StyledWidthFormItem>
            <StyledWidthFormItem
              name="companyID"
              label="Office:"
              hidden={!showOfficePicker}
              required={showOfficePicker}
              rules={
                showOfficePicker
                  ? [{ required: true, message: "Office is required" }]
                  : []
              }
              $width="50%"
            >
              <Select
                placeholder="Select office"
                aria-label="Office"
                options={ownerBranches.map((branch) => ({
                  label: branch.name,
                  value: branch.id
                }))}
              />
            </StyledWidthFormItem>
            <StyledLineBreak />
            <StyledWidthFormItem
              name="referenceID"
              label="Client ID:"
              $width="50%"
            >
              <Input placeholder="Enter client ID" />
            </StyledWidthFormItem>
          </StyledFieldset>
          <StyledFieldset title="Contact Details">
            <StyledWidthFormItem
              name="firstName"
              label="Firstname:"
              rules={[
                { whitespace: true, message: "First Name cannot be empty" }
              ]}
              $width="33.33%"
              required
            >
              <Input />
            </StyledWidthFormItem>
            <StyledWidthFormItem
              name="lastName"
              label="Lastname:"
              rules={[
                { whitespace: true, message: "Last Name cannot be empty" }
              ]}
              $width="33.33%"
              required
            >
              <Input />
            </StyledWidthFormItem>
            <StyledWidthFormItem
              name="dateOfBirth"
              label="Date of birth:"
              $width="33.33%"
              rules={[
                { required: true, message: "Birth date is required" },
                {
                  validator: (_, value) => {
                    if (value) {
                      const result = noFutureDates(value.format("DD/MM/YYYY"))

                      if (result === true) {
                        return Promise.resolve()
                      }

                      return Promise.reject(result)
                    }

                    return Promise.resolve()
                  }
                },
                {
                  validator: (_, value) => {
                    if (value) {
                      const result = validateMinAge(
                        value.format("DD/MM/YYYY"),
                        "Client must be 18 years old or older",
                        18
                      )

                      if (result === true) {
                        return Promise.resolve()
                      }

                      return Promise.reject(result)
                    }

                    return Promise.resolve()
                  }
                }
              ]}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
            </StyledWidthFormItem>
            <StyledWidthFormItem
              name="emailAddress"
              label="Email address:"
              rules={[
                { required: true, message: "Email address is required" },
                {
                  validator: (_, value) => {
                    if (value) {
                      const trimmedValue = (value || "").trim()
                      if (!trimmedValue) {
                        return Promise.reject("Email address is required")
                      }
                      const result = mustBeValidEmail(trimmedValue)

                      if (result === undefined) {
                        return Promise.resolve()
                      }

                      return Promise.reject(result)
                    }

                    return Promise.resolve()
                  }
                }
              ]}
              $width="50%"
            >
              <Input type="email" />
            </StyledWidthFormItem>
            <StyledWidthFormItem
              name="phoneNumber"
              label="Telephone:"
              rules={[
                {
                  validator: (_, value) => {
                    const newValue = (value || "").trim()
                    if (!newValue) {
                      return Promise.resolve()
                    }

                    if (flags.enableAUPhoneNumberValidation) {
                      const result = mustBeValidPhoneNumber(newValue, "AU")

                      if (result !== undefined) {
                        return Promise.reject(result)
                      }
                    }

                    return Promise.resolve()
                  }
                }
              ]}
              hasFeedback={isCheckingPhoneNumber}
              validateStatus={isCheckingPhoneNumber ? "validating" : undefined}
              $width="50%"
            >
              <Input
                onBlur={async (e) => {
                  const newValue = (e.target.value || "").trim()

                  if (!newValue || !flags.enablePhoneNumberLookup) {
                    return
                  }

                  // Skip the Twilio call if the number is already
                  // known to be format-invalid - the synchronous rule above
                  // will be showing that error already.
                  if (form.getFieldError("phoneNumber").length > 0) {
                    return
                  }

                  setIsCheckingPhoneNumber(true)

                  try {
                    const lookupResult = await lookupPhoneNumber(newValue)

                    form.setFields([
                      {
                        name: "phoneNumber",
                        errors: lookupResult.isValid
                          ? []
                          : [
                              "We couldn't verify this phone number - please check it's correct"
                            ]
                      }
                    ])
                  } catch {
                    // If the lookup call itself fails (network issue, Twilio outage)
                    // don't block the form on a check we couldn't complete - the
                    // format check above still applies regardless.
                  } finally {
                    setIsCheckingPhoneNumber(false)
                  }
                }}
              />
            </StyledWidthFormItem>
            <StyledWidthFormItem
              name={["contactAddress"]}
              label="Contact Address"
              rules={[
                {
                  validator: (_, v) => {
                    return new Promise((resolve, reject) => {
                      if (v?.line1 && v?.locality && v?.postalCode) {
                        resolve(true)
                      }

                      reject("Contact address field is required.")
                    })
                  }
                }
              ]}
              $width="100%"
              required
            >
              <UniversalAddress name={"contactAddress"}>
                <UniversalAddress.ManualInput />
              </UniversalAddress>
            </StyledWidthFormItem>
          </StyledFieldset>

          <div>
            {typeof onBack === "function" && (
              <>
                <Button
                  style={{ marginRight: "15px" }}
                  size="large"
                  type="link"
                  onClick={() => onBack()}
                >
                  Go back
                </Button>
              </>
            )}
            {typeof onCloseForm === "function" && (
              <>
                <Button
                  style={{ marginRight: "15px" }}
                  size="large"
                  type="link"
                  onClick={() => onCloseForm()}
                >
                  Back to clients
                </Button>
              </>
            )}
            <Button
              size="large"
              type="primary"
              onClick={() => form.submit()}
              loading={isLoading}
              disabled={isCheckingPhoneNumber}
            >
              {isEditMode ? "Save and continue" : "Create"}
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  )
}

export interface IClientData {
  policyholderType: PolicyholderType
  businessName: string | null | undefined
  title: string | null | undefined
  firstName: string | null | undefined
  lastName: string | null | undefined
  dateOfBirth: Moment | null | undefined
  phoneNumber: string | null | undefined
  emailAddress: string | null | undefined
  contactAddress?: {
    line1: string | null
    line2?: string | null
    line3?: string | null
    locality: string | null
    province?: string | null
    postalCode: string | null
    country?: string | null
    provinceReferenceID?: string | null
    uniqueReference?: string | null
  }
  ownerID: string | null | undefined
  referenceID: string | null | undefined
  /** the account manager's branch (office) - only set when creating a client */
  companyID?: string | null
}

interface IClientFormProps {
  client?: IPolicyholder
  isLoading?: boolean
  onFinish: (data: IClientData) => void
  onCloseForm?: () => void
  onBack?: () => void
  isEditMode?: boolean
}

export default ClientForm
